'use client'

import { useEffect, useState } from 'react'
import type { AppMaintenanceStatus } from './types'
import {
    DEFAULT_MAINTENANCE_STATUS_API_PATH,
    DEFAULT_MAINTENANCE_STATUS_POLL_INTERVAL_MS,
} from './defaults'
import { fetchMaintenanceStatusPoll } from './fetchMaintenanceStatusPoll'

export interface UseMaintenanceStatusPollingOptions {
    redirectUrl: string
    replace: (href: string) => void
    statusUrl?: string
    pollIntervalMs?: number
}

export const useMaintenanceStatusPolling = ({
    redirectUrl,
    replace,
    statusUrl = DEFAULT_MAINTENANCE_STATUS_API_PATH,
    pollIntervalMs = DEFAULT_MAINTENANCE_STATUS_POLL_INTERVAL_MS,
}: UseMaintenanceStatusPollingOptions): AppMaintenanceStatus | null => {
    const [status, setStatus] = useState<AppMaintenanceStatus | null>(null)

    useEffect(() => {
        const controller = new AbortController()
        const { signal } = controller

        const tick = () => {
            void fetchMaintenanceStatusPoll(signal, statusUrl)
                .then((result) => {
                    if (signal.aborted) {
                        return
                    }
                    if (result.outcome === 'notInMaintenance') {
                        replace(redirectUrl)
                        return
                    }
                    if (result.outcome === 'inMaintenance') {
                        setStatus(result.status)
                    }
                })
                .catch((error) => {
                    if (signal.aborted || (error instanceof Error && error.name === 'AbortError')) {
                        return
                    }
                })
        }

        tick()
        const intervalId = setInterval(tick, pollIntervalMs)

        return () => {
            controller.abort()
            clearInterval(intervalId)
        }
    }, [pollIntervalMs, redirectUrl, replace, statusUrl])

    return status
}
